import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Shield, ChevronRight, ChevronLeft, Loader2, Wine, Pill, AlertTriangle, Gavel } from "lucide-react";
import { toast } from "sonner";
import { SpurwechselLogo, SpurwechselIcon } from "@/components/SpurwechselLogo";

const REASONS = [
  { value: "alkohol", label: "Alkohol", desc: "Alkoholauffälligkeit im Straßenverkehr", icon: Wine },
  { value: "drogen", label: "Drogen", desc: "Drogenauffälligkeit", icon: Pill },
  { value: "punkte", label: "Punkte", desc: "Zu viele Verkehrsverstöße", icon: AlertTriangle },
  { value: "straftaten", label: "Straftaten", desc: "Straftaten im Zusammenhang mit dem Verkehr", icon: Gavel },
];

const LEVELS = [
  { value: "anfaenger", label: "Erstmalig", desc: "Ich bereite mich zum ersten Mal vor" },
  { value: "fortgeschritten", label: "Fortgeschritten", desc: "Ich habe bereits Erfahrung mit der Vorbereitung" },
  { value: "durchgefallen", label: "Wiederholung", desc: "Ich bin bereits einmal durchgefallen" },
];

const LICENSE_TYPES = [
  { value: "pkw", label: "PKW (Klasse B)" },
  { value: "lkw", label: "LKW (Klasse C/CE)" },
  { value: "motorrad", label: "Motorrad (Klasse A)" },
  { value: "sonstige", label: "Sonstige" },
];

const GOALS = [
  { id: "sicher_auftreten", label: "Sicheres Auftreten im Gespräch" },
  { id: "selbstreflexion", label: "Bessere Selbstreflexion" },
  { id: "argumentation", label: "Klare Argumentation aufbauen" },
  { id: "ausweichen_vermeiden", label: "Ausweichverhalten erkennen" },
  { id: "rueckfall_plan", label: "Rückfallprävention entwickeln" },
  { id: "verantwortung", label: "Eigenverantwortung stärken" },
];

export default function OnboardingPage() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ mpu_reason: "", situation_description: "", previous_mpu: false, experience_level: "", goals: [], mpu_date: "", license_type: "", incident_date: "" });
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const progress = ((step + 1) / 4) * 100;
  const canNext = step === 0 ? data.mpu_reason : step === 1 ? data.situation_description.length > 10 : step === 2 ? data.experience_level : true;

  const [showTransition, setShowTransition] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding", data);
      await refreshUser();
      // Zeige Übergangs-Bildschirm bevor Session startet
      setShowTransition(true);
      // Nach Onboarding → Lernpfad (nicht direkt in Session)
      setTimeout(() => navigate("/lernpfad"), 2500);
    } catch (err) {
      toast.error("Fehler beim Speichern");
      setShowTransition(false);
    } finally {
      setLoading(false);
    }
  };

  const toggleGoal = (id) => {
    setData(d => ({ ...d, goals: d.goals.includes(id) ? d.goals.filter(g => g !== id) : [...d.goals, id] }));
  };

  if (showTransition) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4" data-testid="onboarding-transition">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <SpurwechselIcon className="w-8 h-8 animate-pulse" />
          </div>
          <h2 className="font-heading text-2xl font-bold mb-3">Ihr Lernpfad wird erstellt</h2>
          <p className="font-body text-sm text-muted-foreground leading-relaxed">
            Basierend auf Ihren Angaben erstellen wir Ihren persönlichen Vorbereitungsplan.
            Sie starten mit einem Kennenlerngespräch — in Ihrem Tempo, ohne Druck.
          </p>
          <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mt-6" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-8 page-transition" data-testid="onboarding-page">
      <div className="w-full max-w-xl">
        <div className="text-center mb-8">
          <SpurwechselLogo size="default" className="justify-center mx-auto mb-3" />
          <h1 className="font-heading text-2xl font-bold">Ihr persönliches Profil</h1>
          <p className="font-body text-sm text-muted-foreground mt-1">Damit wir Ihr Training optimal anpassen können</p>
        </div>

        <Progress value={progress} className="mb-6 h-2" data-testid="onboarding-progress" />
        <p className="font-body text-xs text-muted-foreground text-center mb-6">Schritt {step + 1} von 4</p>

        <Card className="shadow-sm border-border/50">
          <CardContent className="pt-6">
            {step === 0 && (
              <div className="space-y-4" data-testid="onboarding-step-0">
                <CardTitle className="font-heading text-xl">Was ist der Anlass für Ihre MPU?</CardTitle>
                <CardDescription className="font-body text-sm">Wählen Sie den Hauptgrund aus</CardDescription>
                <RadioGroup value={data.mpu_reason} onValueChange={v => setData(d => ({ ...d, mpu_reason: v }))}>
                  {REASONS.map(r => (
                    <Label key={r.value} htmlFor={r.value} className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all duration-200 ${data.mpu_reason === r.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
                      <RadioGroupItem value={r.value} id={r.value} data-testid={`reason-${r.value}`} />
                      <r.icon className="w-5 h-5 text-primary flex-shrink-0" />
                      <div>
                        <p className="font-body text-sm font-medium">{r.label}</p>
                        <p className="font-body text-xs text-muted-foreground">{r.desc}</p>
                      </div>
                    </Label>
                  ))}
                </RadioGroup>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4" data-testid="onboarding-step-1">
                <CardTitle className="font-heading text-xl">Ihre persönliche Situation</CardTitle>
                <CardDescription className="font-body text-sm">Beschreiben Sie kurz, was passiert ist. Diese Information hilft uns, das Training zu personalisieren.</CardDescription>
                <Textarea
                  value={data.situation_description}
                  onChange={e => setData(d => ({ ...d, situation_description: e.target.value }))}
                  placeholder="Beispiel: Ich wurde im März 2024 mit 1,2 Promille angehalten. Davor hatte ich regelmäßig am Wochenende getrunken..."
                  className="min-h-[140px] font-body text-sm"
                  data-testid="situation-textarea"
                />
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <Checkbox id="prev" checked={data.previous_mpu} onCheckedChange={v => setData(d => ({ ...d, previous_mpu: v }))} data-testid="previous-mpu-checkbox" />
                  <Label htmlFor="prev" className="font-body text-sm cursor-pointer">Ich hatte bereits eine MPU-Begutachtung</Label>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4" data-testid="onboarding-step-2">
                <CardTitle className="font-heading text-xl">Ihr Erfahrungsstand</CardTitle>
                <CardDescription className="font-body text-sm">Wie weit sind Sie in Ihrer Vorbereitung?</CardDescription>
                <RadioGroup value={data.experience_level} onValueChange={v => setData(d => ({ ...d, experience_level: v }))}>
                  {LEVELS.map(l => (
                    <Label key={l.value} htmlFor={l.value} className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all duration-200 ${data.experience_level === l.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
                      <RadioGroupItem value={l.value} id={l.value} data-testid={`level-${l.value}`} />
                      <div>
                        <p className="font-body text-sm font-medium">{l.label}</p>
                        <p className="font-body text-xs text-muted-foreground">{l.desc}</p>
                      </div>
                    </Label>
                  ))}
                </RadioGroup>
                <div className="border-t border-border/50 pt-4 space-y-3">
                  <div>
                    <Label className="font-body text-sm">Führerschein-Klasse (optional)</Label>
                    <RadioGroup value={data.license_type} onValueChange={v => setData(d => ({ ...d, license_type: v }))} className="flex flex-wrap gap-2 mt-1.5">
                      {LICENSE_TYPES.map(l => (
                        <Label key={l.value} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm font-body transition-all ${data.license_type === l.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
                          <RadioGroupItem value={l.value} id={`lic-${l.value}`} data-testid={`license-${l.value}`} className="sr-only" />
                          {l.label}
                        </Label>
                      ))}
                    </RadioGroup>
                  </div>
                  <div>
                    <Label className="font-body text-sm">Wann war der Vorfall? (optional)</Label>
                    <Input type="month" value={data.incident_date} onChange={e => setData(d => ({ ...d, incident_date: e.target.value }))} className="mt-1 font-body max-w-xs" data-testid="incident-date-input" />
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4" data-testid="onboarding-step-3">
                <CardTitle className="font-heading text-xl">Ihre Ziele</CardTitle>
                <CardDescription className="font-body text-sm">Was möchten Sie mit dem Training erreichen?</CardDescription>
                <div className="space-y-2">
                  {GOALS.map(g => (
                    <Label key={g.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all duration-200 ${data.goals.includes(g.id) ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
                      <Checkbox checked={data.goals.includes(g.id)} onCheckedChange={() => toggleGoal(g.id)} data-testid={`goal-${g.id}`} />
                      <span className="font-body text-sm">{g.label}</span>
                    </Label>
                  ))}
                </div>
                <div>
                  <Label className="font-body text-sm">Geplanter MPU-Termin (optional)</Label>
                  <Input type="date" value={data.mpu_date} onChange={e => setData(d => ({ ...d, mpu_date: e.target.value }))} className="mt-1 font-body" data-testid="mpu-date-input" />
                </div>
              </div>
            )}

            <div className="flex justify-between mt-8">
              <Button variant="ghost" onClick={() => setStep(s => s - 1)} disabled={step === 0} className="font-body" data-testid="onboarding-back-btn">
                <ChevronLeft className="w-4 h-4 mr-1" /> Zurueck
              </Button>
              {step < 3 ? (
                <Button onClick={() => setStep(s => s + 1)} disabled={!canNext} className="font-body bg-primary text-primary-foreground hover:bg-primary/90" data-testid="onboarding-next-btn">
                  Weiter <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={loading} className="font-body bg-primary text-primary-foreground hover:bg-primary/90" data-testid="onboarding-finish-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Training starten <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
